import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import beTwMerge from '@/utils/beTwMerge';

let coincidenceCounter = 0;
let priorKey = null;
let currentSelectedOpt: null | Option;

export type Option = { label: string; value: string | null | number };
type SelectProps = {
  items: Option[];
  className?: string;
  placeholder: string;
  selectedOption?: Option | null;
  register?: any;
  tabIndex?: number;
  onChange: (option: Option) => void;
  showDefaultValue?: boolean;
  parentRef?: React.RefObject<HTMLElement>;
};
export default function Select({
  className,
  items,
  onChange,
  placeholder,
  selectedOption,
  tabIndex,
  register = {},
  showDefaultValue = true,
  parentRef,
}: SelectProps) {
  const defaultValue = { label: placeholder || 'Select', value: '' };
  const [isOpen, setIsOpen] = useState(false);
  const options = showDefaultValue ? [defaultValue, ...(items || [])] : [...(items || [])];
  const [selected, setSelected] = useState<Option>(selectedOption || defaultValue);
  const ref = useRef<HTMLDivElement | null>(null);
  const refBtn = useRef<HTMLButtonElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const [dropdownPosition, setDropdownPosition] = useState('');
  const optionHeight = 45;

  const onKeyUp = e => {
    if ([38, 40].includes(e.keyCode) || (e.keyCode >= 65 && e.keyCode <= 90)) {
      onChange(selected);
    }
  };

  const onKeyDown = e => {
    if (e.keyCode === 9) return;

    e.preventDefault();
    let selectedOpt: null | Option = null;

    if (e.keyCode === 13 || e.keyCode === 27) {
      isOpen && onChange(selected);
      setIsOpen(open => !open);
    }

    // naviagte the list with arrow keys and focus the selected item
    else if (e.keyCode === 38 || e.keyCode === 40) {
      const list = ref.current?.querySelectorAll('div[tabindex]') || [];
      const currentIndex = options.findIndex(option => option.value === selected.value);
      const nextIndex = e.keyCode === 38 ? currentIndex - 1 : currentIndex + 1;

      const nextOption = list[nextIndex] as any;
      if (nextIndex >= 0 && nextIndex < options.length) {
        selectedOpt = options[nextIndex];
        nextOption.focus();
        setTimeout(() => refBtn.current?.focus(), 1);
      }
    }
    // search by key letter
    else if (e.keyCode >= 65 && e.keyCode <= 90) {
      const key = e.key.toLowerCase();
      const list = ref.current?.querySelectorAll('div[tabindex]') || [];
      coincidenceCounter = priorKey === key ? coincidenceCounter + 1 : 0;
      const opts = options.filter(option => option.label.toLowerCase().startsWith(key));
      if (coincidenceCounter >= opts.length) coincidenceCounter = 0;

      const indexes = options.reduce((acc, option, ind) => {
        if (option.label.toLowerCase().startsWith(key)) {
          acc.push(ind);
        }
        return acc;
      }, [] as number[]);

      priorKey = key;
      const nextOption = list[indexes[coincidenceCounter]] as any;
      if (nextOption) {
        nextOption.focus();
        selectedOpt = options.find(option => option.label === nextOption.textContent)!;
        setTimeout(() => refBtn.current?.focus(), 1);
      }
    }

    selectedOpt && setSelected(selectedOpt as Option);
  };

  useEffect(() => {
    setSelected(selectedOption || defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  // this useEffect sets a max-height for the options div if there is not enough space to show,
  // and changes the direction of the options above the button if is to close to the border of the screen
  useEffect(() => {
    const updatePosition = () => {
      if (!refBtn.current || !isOpen) {
        setDropdownPosition('');
        return;
      }

      const buttonRect = refBtn.current.getBoundingClientRect();

      // Get parent boundaries
      let parentBottom = window.innerHeight;
      if (parentRef?.current) {
        const parentRect = parentRef.current.getBoundingClientRect();
        // parentBottom = Math.min(parentRect.bottom, window.innerHeight);
        parentBottom = parentRect.bottom;
      }

      const bottomSpace = parentBottom - buttonRect.bottom;
      const topSpace = buttonRect.top;
      const totalDropdownHeight = options.length * optionHeight + 5;

      // Minimum space needed is at least one option height plus some padding
      const minSpace = optionHeight * 2 + 20;

      // If we have more than one option height of space, use scrolling
      if (bottomSpace >= minSpace) {
        setDropdownPosition('bottom');
        // Set max height to either full content or available space
        setMaxHeight(Math.min(totalDropdownHeight, bottomSpace - 20));
      } else if (topSpace >= minSpace) {
        setDropdownPosition('top');
        setMaxHeight(Math.min(totalDropdownHeight, topSpace - 20));
      } else if (bottomSpace >= topSpace) {
        setDropdownPosition('bottom');
        setMaxHeight(bottomSpace - 20);
      } else {
        setDropdownPosition('top');
        setMaxHeight(topSpace - 20);
      }
    };

    updatePosition();
  }, [isOpen, options.length, parentRef]);

  return (
    <div
      className={beTwMerge('tw-relative tw-cursor-pointer tw-group', className)}
      ref={ref}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      role="none"
    >
      <input type="hidden" {...register} value={selected.value} />
      <button
        tabIndex={tabIndex || 0}
        className="tw-w-full tw-text-left tw-bg-white tw-border tw-border-none tw-p-0 tw-outline-none tw-text-[#333]"
        type="button"
        ref={refBtn}
        onClick={() => {
          currentSelectedOpt = selected;
          setIsOpen(v => !v);
        }}
      >
        <div
          className={classNames(
            { '!tw-border-b-transparent': dropdownPosition === 'bottom' },
            { '!tw-border-t-transparent': dropdownPosition === 'top' },
            'tw-relative tw-text-sm tw-py-[9px] tw-px-2 tw-border tw-border-solid tw-border-[#ccc] group-[.is-error]:tw-border-error group-[.is-error]:tw-text-error'
          )}
        >
          <span
            className={classNames('tw-absolute arrow-bottom tw-ml-[5px] tw-right-[15px] tw-mt-1', {
              'tw-rotate-180': isOpen,
            })}
          />
          {selected?.label}
        </div>
      </button>

      <div
        className={classNames(
          'tw-absolute tw-bg-white tw-w-full tw-border tw-border-solid tw-border-[#ccc] tw-z-50 tw-max-h-[386px] tw-overflow-auto',
          { hidden: !isOpen },
          { 'tw-top-full tw-border-t-0 group-[.is-error]:tw-border-error': dropdownPosition === 'bottom' },
          { 'tw-bottom-full tw-border-b-0 group-[.is-error]:tw-border-error': dropdownPosition === 'top' }
        )}
        style={{ maxHeight }}
      >
        {options?.map((option, index) => (
          <div
            key={index}
            onClick={() => {
              setSelected(option);
              setIsOpen(false);
              currentSelectedOpt?.value !== option.value && onChange(option);
            }}
            role="button"
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setSelected(option);
                setIsOpen(false);
                currentSelectedOpt?.value !== option.value && onChange(option);
              }
            }}
            tabIndex={index + 1}
            data-selected={option.value === selected.value}
            className={classNames(
              { 'tw-bg-[#ececec]': selected.value === option.value },
              'tw-text-sm hover:tw-bg-[#ececec] tw-py-2 tw-px-2 tw-outline-none'
            )}
          >
            {option.label}
          </div>
        ))}
      </div>
    </div>
  );
}
